import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import LatteButton from './LatteButton';

const MobileModal = ({ 
  isOpen = false,
  onClose,
  title,
  children,
  actions,
  hideCloseButton = false,
  className = '',
  fullScreen = false,
  slideUp = true
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={`mobile-modal-wrapper ${isOpen ? 'open' : ''}`}>
      <div 
        className="mobile-modal-overlay"
        onClick={onClose}
      />
      
      <div className={`
        mobile-modal-container 
        ${fullScreen ? 'full-screen' : ''} 
        ${slideUp ? 'slide-up' : 'fade-in'}
        ${className}
      `}>
        {/* 상단 드래그 인디케이터 */}
        {!fullScreen && (
          <div className="mobile-modal-drag-indicator">
            <div className="drag-bar" />
          </div>
        )}
        
        {/* 헤더 */}
        {title && (
          <div className="mobile-modal-header">
            <h3 className="mobile-modal-title">{title}</h3>
            {!hideCloseButton && (
              <button
                onClick={onClose}
                className="mobile-modal-close"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}
        
        {/* 컨텐츠 */}
        <div className="mobile-modal-content">
          {children}
        </div>
        
        {/* 액션 버튼들 */}
        {actions && actions.length > 0 && (
          <div className="mobile-modal-actions">
            {actions.map((action, index) => (
              <LatteButton
                key={index}
                onClick={action.onClick}
                variant={action.variant || 'primary'}
                disabled={action.disabled}
                className={`mobile-modal-action-button ${action.className || ''}`}
              >
                {action.icon && <span className="button-icon">{action.icon}</span>}
                {action.label}
              </LatteButton>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const MobileAlertModal = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info', // 'info' | 'warning' | 'error' | 'success'
  confirmText = '확인',
  onConfirm
}) => {
  const iconMap = {
    info: '💡',
    warning: '⚠️',
    error: '❌',
    success: '✅'
  };

  const colorMap = {
    info: 'blue',
    warning: 'amber',
    error: 'red',
    success: 'green'
  };

  const color = colorMap[type];
  const icon = iconMap[type];

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className="mobile-alert-modal"
    >
      <div className="mobile-alert-content">
        <p className="mobile-alert-message">{message}</p>
      </div>
      
      <div className="mobile-alert-actions">
        <LatteButton
          onClick={() => {
            if (onConfirm) onConfirm();
            onClose();
          }}
          variant="primary"
          className="w-full"
        >
          {confirmText}
        </LatteButton>
      </div>
    </MobileModal>
  );
};

export const MobileConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = '확인',
  cancelText = '취소',
  type = 'warning' // 'warning' | 'danger'
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      {/* 오버레이 */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* 모달 컨텐츠 */}
      <div className="relative bg-white w-full sm:max-w-md mx-auto rounded-t-2xl sm:rounded-2xl shadow-xl transform transition-all">
        {/* 헤더 */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">
              {title}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
        
        {/* 바디 */}
        <div className="px-6 pb-6">
          {/* 경고 메시지 */}
          <div className={`${type === 'danger' ? 'bg-red-50' : 'bg-amber-50'} rounded-lg p-4`}>
            <p className={`text-sm ${type === 'danger' ? 'text-red-800' : 'text-amber-800'} font-medium whitespace-pre-line`}>
              {message}
            </p>
          </div>
        </div>
        
        {/* 푸터 */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <LatteButton
            onClick={onClose}
            variant="secondary"
            className="flex-1"
          >
            {cancelText}
          </LatteButton>
          <LatteButton
            onClick={() => {
              onConfirm();
              onClose();
            }}
            variant={type === 'danger' ? 'danger' : 'primary'}
            className="flex-1"
          >
            {confirmText}
          </LatteButton>
        </div>
      </div>
    </div>
  );
};

export default MobileModal;